import { AccountInfo, IPublicClientApplication } from "@azure/msal-browser";
import { loginRequest } from "./authConfig";

const SITE_DISCOVERY_URL = "https://graph.microsoft.com/v1.0/sites/leerowsro.sharepoint.com:/sites/LRPlanning";

let cachedSiteId: string | null = null;

async function getToken(instance: IPublicClientApplication, account: AccountInfo): Promise<string> {
  const response = await instance.acquireTokenSilent({ ...loginRequest, account });
  return response.accessToken;
}

async function apiGet(url: string, token: string) {
  const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!r.ok) throw new Error(`GET ${url} failed: ${r.status}`);
  return r.json();
}

async function apiPost(url: string, token: string, body: any) {
  const r = await fetch(url, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(`POST ${url} failed: ${r.status}`);
  return r.json();
}

async function apiPatch(url: string, token: string, body: any) {
  const r = await fetch(url, {
    method: "PATCH",
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(`PATCH ${url} failed: ${r.status}`);
}

async function apiDelete(url: string, token: string) {
  const r = await fetch(url, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
  if (!r.ok) throw new Error(`DELETE ${url} failed: ${r.status}`);
}

export async function getSiteId(instance: IPublicClientApplication, account: AccountInfo): Promise<string> {
  if (cachedSiteId) return cachedSiteId;
  console.log('[getSiteId] fetching from', SITE_DISCOVERY_URL);
  const token = await getToken(instance, account);
  const data = await apiGet(SITE_DISCOVERY_URL, token);
  console.log('[getSiteId] response:', data);
  cachedSiteId = data.id;
  console.log('[getSiteId] cached site ID:', cachedSiteId);
  return cachedSiteId as string;
}

function base(siteId: string) {
  return `https://graph.microsoft.com/v1.0/sites/${siteId}`;
}

// ─── Workers ──────────────────────────────────────────────────────────────────

export async function getWorkers(instance: IPublicClientApplication, account: AccountInfo) {
  console.log('[getWorkers] start');
  const [token, siteId] = await Promise.all([getToken(instance, account), getSiteId(instance, account)]);
  const url = `${base(siteId)}/lists/Workers/items?$expand=fields&$top=100`;
  console.log('[getWorkers] GET', url);
  const data = await apiGet(url, token);
  console.log('[getWorkers] raw data:', data);
  console.log('[getWorkers] first item sample:', data.value?.[0]);
  const mapped = data.value
    .map((i: any) => ({ id: i.id, ...i.fields }))
    .sort((a: any, b: any) => (a.SortOrder || 0) - (b.SortOrder || 0));
  console.log('[getWorkers] mapped:', mapped);
  return mapped;
}

export async function addWorker(instance: IPublicClientApplication, account: AccountInfo, fullName: string, sortOrder: number) {
  console.log('[addWorker]', fullName, sortOrder);
  const [token, siteId] = await Promise.all([getToken(instance, account), getSiteId(instance, account)]);
  const result = await apiPost(`${base(siteId)}/lists/Workers/items`, token, {
    fields: { Title: fullName, FullName: fullName, SortOrder: sortOrder, IsActive: true },
  });
  return { id: result.id, Title: fullName, FullName: fullName, SortOrder: sortOrder, IsActive: true };
}

export async function updateWorker(instance: IPublicClientApplication, account: AccountInfo, id: string, fields: Partial<{ FullName: string; SortOrder: number; IsActive: boolean }>) {
  const [token, siteId] = await Promise.all([getToken(instance, account), getSiteId(instance, account)]);
  const spFields: any = { ...fields };
  if (fields.FullName) spFields.Title = fields.FullName;
  await apiPatch(`${base(siteId)}/lists/Workers/items/${id}/fields`, token, spFields);
}

export async function deleteWorker(instance: IPublicClientApplication, account: AccountInfo, id: string) {
  const [token, siteId] = await Promise.all([getToken(instance, account), getSiteId(instance, account)]);
  await apiDelete(`${base(siteId)}/lists/Workers/items/${id}`, token);
}

export async function renameWorkerInEntries(
  instance: IPublicClientApplication,
  account: AccountInfo,
  oldName: string,
  newName: string,
): Promise<{ updated: number; failed: number }> {
  const [token, siteId] = await Promise.all([getToken(instance, account), getSiteId(instance, account)]);
  const trimmedOld = oldName.trim();
  console.log(`[renameWorkerInEntries] "${trimmedOld}" → "${newName}"`);

  const data = await apiGet(`${base(siteId)}/lists/PlannerEntries/items?$expand=fields&$top=5000`, token);
  const total = (data.value as any[]).length;
  console.log(`[renameWorkerInEntries] loaded ${total} entries total`);

  const affected = (data.value as any[]).filter(i => (i.fields.WorkerName || '').trim() === trimmedOld);
  console.log(`[renameWorkerInEntries] affected entries: ${affected.length}`);

  let updated = 0;
  let failed = 0;
  for (const i of affected) {
    const entryDate = String(i.fields.EntryDate || '').slice(0, 10);
    const patchFields = { WorkerName: newName, Title: `${newName}-${entryDate}` };
    console.log(`[renameWorkerInEntries] PATCH id=${i.id} EntryDate=${entryDate}`);
    try {
      await apiPatch(`${base(siteId)}/lists/PlannerEntries/items/${i.id}/fields`, token, patchFields);
      updated++;
    } catch (e) {
      console.error(`[renameWorkerInEntries] PATCH failed id=${i.id}:`, e);
      failed++;
    }
  }

  console.log(`[renameWorkerInEntries] done, updated ${updated}, failed ${failed}`);
  return { updated, failed };
}

// ─── Projects ─────────────────────────────────────────────────────────────────

export async function getProjects(instance: IPublicClientApplication, account: AccountInfo) {
  console.log('[getProjects] start');
  const [token, siteId] = await Promise.all([getToken(instance, account), getSiteId(instance, account)]);
  const url = `${base(siteId)}/lists/Projects/items?$expand=fields&$top=100`;
  console.log('[getProjects] GET', url);
  const data = await apiGet(url, token);
  console.log('[getProjects] raw data:', data);
  const mapped = data.value
    .map((i: any) => ({ id: i.id, ...i.fields }))
    .sort((a: any, b: any) => (a.SortOrder || 0) - (b.SortOrder || 0));
  console.log('[getProjects] mapped:', mapped);
  return mapped;
}

export async function addProject(instance: IPublicClientApplication, account: AccountInfo, name: string, sortOrder: number) {
  const [token, siteId] = await Promise.all([getToken(instance, account), getSiteId(instance, account)]);
  const result = await apiPost(`${base(siteId)}/lists/Projects/items`, token, { fields: { Title: name, SortOrder: sortOrder } });
  return { id: result.id, Title: name, SortOrder: sortOrder };
}

export async function updateProject(instance: IPublicClientApplication, account: AccountInfo, id: string, fields: { Title?: string; SortOrder?: number; StartDate?: string | null; DeadlineDate?: string | null }) {
  const [token, siteId] = await Promise.all([getToken(instance, account), getSiteId(instance, account)]);
  const url = `${base(siteId)}/lists/Projects/items/${id}/fields`;
  console.log('[updateProject] PATCH', url, JSON.stringify(fields));
  const r = await fetch(url, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(fields),
  });
  const text = await r.text();
  console.log('[updateProject] response status:', r.status, 'body:', text);
  if (!r.ok) throw new Error(`PATCH ${url} failed: ${r.status} ${text}`);
}

export async function initProjectDateColumns(instance: IPublicClientApplication, account: AccountInfo) {
  const [token, siteId] = await Promise.all([getToken(instance, account), getSiteId(instance, account)]);
  const url = `${base(siteId)}/lists/Projects/columns`;
  for (const name of ["StartDate", "DeadlineDate"]) {
    console.log(`[initProjectDateColumns] creating column '${name}'`);
    const r = await fetch(url, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ name, dateTime: { displayAs: "default", format: "dateOnly" } }),
    });
    const text = await r.text();
    console.log(`[initProjectDateColumns] '${name}' → status ${r.status}:`, text);
    if (!r.ok && r.status !== 409) {
      throw new Error(`Nelze vytvořit sloupec '${name}': ${r.status} ${text}`);
    }
  }
}

export async function deleteProject(instance: IPublicClientApplication, account: AccountInfo, id: string) {
  const [token, siteId] = await Promise.all([getToken(instance, account), getSiteId(instance, account)]);
  await apiDelete(`${base(siteId)}/lists/Projects/items/${id}`, token);
}

const SEP = '; ';

function splitProjectNames(raw: string): string[] {
  if (!raw) return [];
  if (raw.includes('; ')) return raw.split('; ').map(n => n.trim()).filter(Boolean);
  if (raw.includes(', ')) return raw.split(', ').map(n => n.trim()).filter(Boolean);
  return raw.trim() ? [raw.trim()] : [];
}

export async function renameProjectInEntries(
  instance: IPublicClientApplication,
  account: AccountInfo,
  oldName: string,
  newName: string,
): Promise<{ updated: number; failed: number }> {
  const [token, siteId] = await Promise.all([getToken(instance, account), getSiteId(instance, account)]);
  const trimmedOld = oldName.trim();
  console.log(`[renameProjectInEntries] "${trimmedOld}" → "${newName}"`);

  const data = await apiGet(`${base(siteId)}/lists/PlannerEntries/items?$expand=fields&$top=5000`, token);
  const total = (data.value as any[]).length;
  console.log(`[renameProjectInEntries] loaded ${total} entries`);

  const affected = (data.value as any[]).filter(i =>
    splitProjectNames(i.fields.ProjectNames || '').includes(trimmedOld)
  );
  console.log(`[renameProjectInEntries] affected entries: ${affected.length}`);

  let updated = 0;
  let failed = 0;
  for (const i of affected) {
    const newNames = splitProjectNames(i.fields.ProjectNames as string)
      .map((n: string) => n === trimmedOld ? newName : n)
      .join(SEP);
    console.log(`[renameProjectInEntries] patching id=${i.id}: "${i.fields.ProjectNames}" → "${newNames}"`);
    try {
      await apiPatch(`${base(siteId)}/lists/PlannerEntries/items/${i.id}/fields`, token, { ProjectNames: newNames });
      updated++;
    } catch (e) {
      console.error(`[renameProjectInEntries] PATCH failed id=${i.id}:`, e);
      failed++;
    }
  }

  console.log(`[renameProjectInEntries] done, updated ${updated}, failed ${failed}`);
  return { updated, failed };
}

export async function initProjectListColumns(instance: IPublicClientApplication, account: AccountInfo) {
  const [token, siteId] = await Promise.all([getToken(instance, account), getSiteId(instance, account)]);
  await apiPost(`${base(siteId)}/lists/Projects/columns`, token, {
    name: "SortOrder", number: { decimalPlaces: "none", displayAs: "number" }
  }).catch(() => {});
}

// ─── Cars ─────────────────────────────────────────────────────────────────────

let _carColumnsEnsured = false;

async function ensureCarColumns(token: string, siteId: string) {
  if (_carColumnsEnsured) return;
  const url = `${base(siteId)}/lists/Cars/columns`;
  await Promise.all([
    fetch(url, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: 'TextColor', text: { allowMultipleLines: false } }) }).catch(() => {}),
    fetch(url, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: 'Notes', text: { allowMultipleLines: true, linesForEditing: 6 } }) }).catch(() => {}),
    fetch(url, { method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: 'MultiSelect', boolean: {} }) }).catch(() => {}),
  ]);
  _carColumnsEnsured = true;
}

export async function getCars(instance: IPublicClientApplication, account: AccountInfo) {
  console.log('[getCars] start');
  const [token, siteId] = await Promise.all([getToken(instance, account), getSiteId(instance, account)]);
  await ensureCarColumns(token, siteId);
  const url = `${base(siteId)}/lists/Cars/items?$expand=fields&$top=50`;
  console.log('[getCars] GET', url);
  const data = await apiGet(url, token);
  console.log('[getCars] raw data:', data);
  const mapped = data.value
    .map((i: any) => ({ id: i.id, ...i.fields }))
    .sort((a: any, b: any) => (a.SortOrder || 0) - (b.SortOrder || 0));
  console.log('[getCars] mapped:', mapped);
  return mapped;
}

export async function addCar(instance: IPublicClientApplication, account: AccountInfo, car: { name: string; color: string; textColor: string; sortOrder: number; notes: string; multiSelect: boolean }) {
  const [token, siteId] = await Promise.all([getToken(instance, account), getSiteId(instance, account)]);
  const result = await apiPost(`${base(siteId)}/lists/Cars/items`, token, {
    fields: { Title: car.name, Color: car.color, TextColor: car.textColor, SortOrder: car.sortOrder, Notes: car.notes, MultiSelect: car.multiSelect },
  });
  return { id: result.id, Title: car.name, Color: car.color, TextColor: car.textColor, SortOrder: car.sortOrder, Notes: car.notes, MultiSelect: car.multiSelect };
}

export async function updateCar(instance: IPublicClientApplication, account: AccountInfo, id: string, fields: Partial<{ Title: string; Color: string; TextColor: string; SortOrder: number; Notes: string; MultiSelect: boolean }>) {
  const [token, siteId] = await Promise.all([getToken(instance, account), getSiteId(instance, account)]);
  await apiPatch(`${base(siteId)}/lists/Cars/items/${id}/fields`, token, fields);
}

export async function deleteCar(instance: IPublicClientApplication, account: AccountInfo, id: string) {
  const [token, siteId] = await Promise.all([getToken(instance, account), getSiteId(instance, account)]);
  await apiDelete(`${base(siteId)}/lists/Cars/items/${id}`, token);
}

// Vytvoří sloupce TextColor a Notes v listu Cars (zavolat jednou z CarsModal)
export async function initCarListColumns(instance: IPublicClientApplication, account: AccountInfo) {
  const [token, siteId] = await Promise.all([getToken(instance, account), getSiteId(instance, account)]);
  const url = `${base(siteId)}/lists/Cars/columns`;
  await Promise.all([
    apiPost(url, token, { name: "TextColor", description: "Barva textu", text: { allowMultipleLines: false } }).catch(() => {}),
    apiPost(url, token, { name: "Notes", description: "Poznámky (servis, závady)", text: { allowMultipleLines: true, linesForEditing: 6 } }).catch(() => {}),
    apiPost(url, token, { name: "MultiSelect", description: "Auto lze přiřadit více pracovníkům ve stejný den", boolean: {} }).catch(() => {}),
  ]);
}

// ─── PlannerEntries ───────────────────────────────────────────────────────────

// dates: pole "YYYY-MM-DD" stringů — všechny dny všech týdnů viditelného měsíce
export async function getPlannerEntries(instance: IPublicClientApplication, account: AccountInfo, dates: string[]) {
  const dateSet = new Set(dates);
  console.log('[getPlannerEntries] loading for', dates.length, 'dates, first:', dates[0], 'last:', dates[dates.length - 1]);
  const [token, siteId] = await Promise.all([getToken(instance, account), getSiteId(instance, account)]);
  const url = `${base(siteId)}/lists/PlannerEntries/items?$expand=fields&$top=5000`;
  const data = await apiGet(url, token);
  console.log('[getPlannerEntries] total items in list:', data.value?.length);
  const filtered = data.value.filter((i: any) => {
    const raw = i.fields.EntryDate;
    if (!raw) return false;
    // SharePoint date fields may return "2026-05-01T00:00:00Z" — normalize to YYYY-MM-DD
    return dateSet.has(String(raw).slice(0, 10));
  });
  console.log('[getPlannerEntries] matched by EntryDate:', filtered.length);
  return filtered.map((i: any) => ({
    id: i.id,
    ...i.fields,
    EntryDate: String(i.fields.EntryDate || '').slice(0, 10),
  }));
}

// ─── ChangeLog ────────────────────────────────────────────────────────────────

export interface ChangeLogEntry {
  UserName: string;
  UserEmail: string;
  Action: string;
  WorkerName: string;
  EntryDate: string;
  OldValue: string;
  NewValue: string;
  Timestamp: string;
}

export async function addChangeLog(
  instance: IPublicClientApplication,
  account: AccountInfo,
  entry: ChangeLogEntry,
) {
  console.log('[addChangeLog] entry:', JSON.stringify(entry));
  const [token, siteId] = await Promise.all([getToken(instance, account), getSiteId(instance, account)]);
  const url = `${base(siteId)}/lists/ChangeLog/items`;
  const body = {
    fields: {
      Title: `${entry.Action}-${entry.Timestamp}`,
      UserName: entry.UserName,
      UserEmail: entry.UserEmail,
      Action: entry.Action,
      WorkerName: entry.WorkerName,
      EntryDate: entry.EntryDate ? `${entry.EntryDate.slice(0, 10)}T00:00:00Z` : null,
      OldValue: entry.OldValue,
      NewValue: entry.NewValue,
      Timestamp: entry.Timestamp,
    },
  };
  console.log('[addChangeLog] POST', url, JSON.stringify(body));
  const result = await apiPost(url, token, body);
  console.log('[addChangeLog] response:', JSON.stringify(result));
}

export async function getChangeLogs(
  instance: IPublicClientApplication,
  account: AccountInfo,
) {
  console.log('[getChangeLogs] start');
  const [token, siteId] = await Promise.all([getToken(instance, account), getSiteId(instance, account)]);
  const url = `${base(siteId)}/lists/ChangeLog/items?$expand=fields&$top=5000`;
  console.log('[getChangeLogs] GET', url);
  const data = await apiGet(url, token);
  console.log('[getChangeLogs] načteno záznamů:', data.value?.length, '| @odata.nextLink:', data['@odata.nextLink'] ?? 'none');
  const mapped = (data.value as any[])
    .map((i: any) => ({ id: i.id, ...i.fields }))
    .sort((a: any, b: any) => {
      const ta = a.Timestamp ? new Date(a.Timestamp).getTime() : 0;
      const tb = b.Timestamp ? new Date(b.Timestamp).getTime() : 0;
      return tb - ta;
    });
  console.log('[getChangeLogs] mapped count:', mapped.length, 'first:', JSON.stringify(mapped[0]));
  return mapped;
}

export async function deletePlannerEntry(instance: IPublicClientApplication, account: AccountInfo, id: string) {
  const [token, siteId] = await Promise.all([getToken(instance, account), getSiteId(instance, account)]);
  await apiDelete(`${base(siteId)}/lists/PlannerEntries/items/${id}`, token);
}

// ─── PlannerEntries ───────────────────────────────────────────────────────────

export async function upsertPlannerEntry(
  instance: IPublicClientApplication,
  account: AccountInfo,
  entry: { id?: string; WorkerId: number; WorkerName: string; EntryDate: string; ProjectNames: string; CarId: number; CarColor: string; Year: number; WeekNumber: number }
) {
  console.log('[upsertPlannerEntry]', entry);
  const [token, siteId] = await Promise.all([getToken(instance, account), getSiteId(instance, account)]);
  const fields = {
    Title: `${entry.WorkerName}-${entry.EntryDate}`,
    WorkerId: entry.WorkerId,
    WorkerName: entry.WorkerName,
    EntryDate: entry.EntryDate,
    ProjectNames: entry.ProjectNames,
    CarId: entry.CarId,
    CarColor: entry.CarColor,
    Year: entry.Year,
    WeekNumber: entry.WeekNumber,
  };
  if (entry.id) {
    await apiPatch(`${base(siteId)}/lists/PlannerEntries/items/${entry.id}/fields`, token, fields);
    return entry.id;
  } else {
    const result = await apiPost(`${base(siteId)}/lists/PlannerEntries/items`, token, { fields });
    console.log('[upsertPlannerEntry] created id:', result.id);
    return result.id;
  }
}
